import React from 'react';
import { __ } from '@wordpress/i18n';

// tools
import {
    JOB_STATUS_LABELS,
    extractJobError,
    extractJobResults,
    isTerminalStatus,
} from '../../helpers/aiJobs';

/**
 * The queue of AI jobs of a project, shared by the stem splitter, the mood
 * cover and the generation modals: one row per job, its status, and the audio
 * it produced once it is ready.
 *
 * The list refreshes itself while something is still running (see useAiJobs),
 * so the refresh button is only there for an impatient click.
 *
 * @param {object}   props
 * @param {string}   props.title
 * @param {array}    props.jobs
 * @param {boolean}  props.loading    a request is in flight
 * @param {boolean}  props.waiting    at least one job has not finished
 * @param {function} props.onRefresh
 * @param {function} props.renderLabel  job -> the name shown on the row
 * @param {function} [props.renderExtra] job -> extra controls under the row
 * @param {string}   [props.emptyLabel]
 */
const AiJobList = ({
    title,
    jobs = [],
    loading = false,
    waiting = false,
    onRefresh,
    renderLabel,
    renderExtra,
    emptyLabel = __('Nessuna richiesta ancora.', 'soundbetween'),
}) => (
    <div className="moodCoverModal__list">
        <div className="moodCoverModal__listHead">
            <span>{title}</span>
            <button
                type="button"
                className="moodCoverModal__refresh"
                onClick={onRefresh}
                disabled={loading}>
                {loading ? '…' : __('Aggiorna', 'soundbetween')}
            </button>
        </div>

        {waiting && (
            <p className="moodCoverModal__note--muted">
                {__('Aggiornamento automatico in corso: puoi chiudere, il lavoro continua.', 'soundbetween')}
            </p>
        )}

        {!jobs.length && !loading && (
            <p className="moodCoverModal__empty">{emptyLabel}</p>
        )}

        {jobs.map(job => {
            const status = job.status || 'pending';
            const results = extractJobResults(job.responseJson);
            const error = extractJobError(job.responseJson);

            return (
                <div key={job.id} className="moodCoverModal__item">
                    <div className="moodCoverModal__itemHead">
                        <span className="moodCoverModal__itemMood">{renderLabel(job)}</span>
                        <span className={`moodCoverModal__badge moodCoverModal__badge--${status}`}>
                            {JOB_STATUS_LABELS[status] || status}
                        </span>
                    </div>

                    {results.map(result => (
                        <div key={result.url} className="moodCoverModal__player">
                            {result.label && (
                                <span className="moodCoverModal__itemMood">{result.label}</span>
                            )}
                            <audio controls src={result.url} preload="none"/>
                            <a className="moodCoverModal__download" href={result.url} download>
                                {__('Scarica', 'soundbetween')}
                            </a>
                        </div>
                    ))}

                    {!isTerminalStatus(status) && (
                        <p className="moodCoverModal__note--muted">
                            {__('In coda: verrà elaborata quando il motore AI è attivo.', 'soundbetween')}
                        </p>
                    )}

                    {status === 'completed' && !results.length && (
                        <p className="moodCoverModal__note--muted">
                            {__('Completata, ma il provider non ha restituito nessun file.', 'soundbetween')}
                        </p>
                    )}

                    {error && <p className="moodCoverModal__note--error">{error}</p>}

                    {renderExtra?.(job)}
                </div>
            );
        })}
    </div>
);

export default AiJobList;
